import { isEmptyString } from '@/utils/string';

class OAuthError extends Error {
  provider: string;

  constructor(message: string, provider: string) {
    super(message);

    this.name = 'OAuthError';
    this.provider = provider;
  }
}

interface OAuthOpenOptions extends Record<string, unknown> {
  width?: number;
  height?: number;
}

export interface AuthorizationCodeFlowOptions {
  name: string;
  oauthKeyName: string;
  authorizeUrl: string;
  scope: string;
  clientId: string;
  usePkce?: boolean;
  codeChallengeMethod?: string;
}

export interface OAuthOpenResponse {
  authorizationCode?: string;
  provider?: string;
  oauthKeyName?: string;
  redirectUri?: string;
  codeVerifier?: string;
}

class OAuthProvider {
  public name: string;
  public oauthKeyName: string;
  public authorizeUrl: string;
  public scope: string;
  public clientId: string;
  public redirectUri: string;

  private stateId: string;
  private responseType = 'code';
  private usePkce: boolean;
  private codeChallengeMethod?: string;
  private codeVerifier?: string;

  // Some OAuth providers, such as Google, require the `client_id` to be visible in the URL, forcing us to use a minimum width.
  readonly MINIMUM_WIDTH = 1000;
  readonly MINIMUM_HEIGHT = 650;

  constructor(options: AuthorizationCodeFlowOptions) {
    this.name = options.name;
    this.oauthKeyName = options.oauthKeyName;
    this.authorizeUrl = options.authorizeUrl;
    this.scope = options.scope;
    this.clientId = options.clientId;
    this.redirectUri = `${window.location.origin}/oauth2/redirect`;
    this.stateId = window.crypto.randomUUID();
    this.usePkce = options.usePkce ?? false;
    this.codeChallengeMethod = options.codeChallengeMethod;

    if (this.usePkce && !isEmptyString(this.codeChallengeMethod)) {
      this.codeVerifier = generateCodeVerifier();
    }
  }

  async open({
    width = this.MINIMUM_WIDTH,
    height = this.MINIMUM_HEIGHT,
    ...rest
  }: OAuthOpenOptions = {}): Promise<OAuthOpenResponse> {
    localStorage.setItem('__oauth_request', this.stateId);

    const url = await this.buildURL(this.authorizeUrl, rest);
    const popup = window.open(
      url,
      '_blank',
      `width=${width},height=${height},left=${screen.width / 2 - width / 2},top=${screen.height / 2 - height / 2}`,
    );

    if (popup == null) {
      throw new OAuthError('Unable to open OAuth window', this.name);
    }

    // need to poll for the closed attribute because the `beforeUnload` event
    // only works for same domains. If it's closed before the redirectURL is
    // loaded, then it won't trigger the `beforeUnload` event!
    // https://stackoverflow.com/questions/9388380/capture-the-close-event-of-popup-window-in-javascript
    while (!popup.closed) {
      await new Promise((res) => setTimeout(res, 100));
    }

    const responseUrl = localStorage.getItem(this.stateId);
    localStorage.removeItem(this.stateId);

    const authorizationCode = getOAuthAuthorizationCode({
      name: this.name,
      stateId: this.stateId,
      responseType: this.responseType,
      responseUrl,
    });

    return {
      authorizationCode,
      provider: this.name,
      oauthKeyName: this.oauthKeyName,
      redirectUri: this.redirectUri,
      codeVerifier: this.codeVerifier,
    };
  }

  private async buildURL(baseUrl: string, dynamicParams?: Record<string, unknown>) {
    const payload: Record<string, any> = {
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: this.responseType,
      state: this.stateId,
      ...dynamicParams,
    };

    // Scope is optional on some OAuth APIs. (ex: Salesforce)
    if (this.scope !== '') {
      payload.scope = this.scope;
    }

    if (
      this.usePkce &&
      this.codeVerifier != null &&
      this.codeVerifier !== '' &&
      this.codeChallengeMethod != null &&
      this.codeChallengeMethod !== ''
    ) {
      payload.code_challenge = await generateCodeChallenge(this.codeVerifier, this.codeChallengeMethod);
      payload.code_challenge_method = this.codeChallengeMethod;
    }

    const url = new URL(baseUrl);
    const search = new URLSearchParams();

    for (const [key, value] of Object.entries(payload)) {
      search.set(key, value);
    }

    for (const [key, value] of url.searchParams.entries()) {
      search.set(key, value);
    }

    url.search = search.toString();

    return url.toString();
  }
}

export function getOAuthAuthorizationCode({
  name,
  stateId,
  responseType,
  responseUrl,
}: {
  name: string;
  stateId: string;
  responseType: string;
  responseUrl: string | null;
}) {
  if (responseUrl == null) {
    throw new OAuthError('OAuth response not found', name);
  }

  const res = parseOAuthResponseUrl(responseUrl);

  if (res.state !== stateId) {
    throw new OAuthError("OAuth state response didn't match. Discarding.", name);
  }

  // See if there is error information that we can return the user.
  // https://datatracker.ietf.org/doc/html/rfc6749#section-4.1.2.1
  const redirectError = res.error?.trim() ?? '';
  const redirectErrorDescription = res.error_description?.trim() ?? '';

  if (redirectError !== '' || redirectErrorDescription !== '') {
    let errmsg = 'OAuth error';
    if (redirectErrorDescription !== '') {
      errmsg += `: ${redirectErrorDescription}`;
    }
    if (redirectError !== '') {
      errmsg += ` (${redirectError})`;
    }
    throw new OAuthError(errmsg, name);
  }

  if (res[responseType] == null) {
    throw new OAuthError('OAuth authorization code not found', name);
  }

  return res[responseType];
}

export function parseOAuthResponseUrl(responseUrl: string): Record<string, string> {
  const url = new URL(responseUrl);

  return Object.fromEntries(url.searchParams.entries());
}

export function base64UrlEncode(buffer: Uint8Array): string {
  let binary = '';
  for (const byte of buffer) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

export async function generateCodeChallenge(verifier: string, method: string): Promise<string> {
  if (method === 'plain') {
    return verifier;
  }
  if (method !== 'S256') {
    throw new Error(`Unsupported PKCE code_challenge_method: ${method}`);
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

export async function performAuthorizationCodeFlow(options: AuthorizationCodeFlowOptions): Promise<OAuthOpenResponse> {
  return await new OAuthProvider(options).open();
}
